'use client';

import { redirect } from 'next/navigation';
import { type ReactNode } from 'react';

import { usePDPContext } from '@/app/[locale]/product/_contexts/PDPProvider';
import WizardStateProvider from '@/app/[locale]/product/_contexts/WizardStateProvider';
import useCurrentRoute from '@/app/[locale]/product/_hooks/useCurrentRoute';
import type CurrentRouteValues from '@/app/[locale]/product/_hooks/useCurrentRoute/types';
import checkCurrentRoute from '@/app/[locale]/product/_utils/checkCurrentRoute';
import { POP_PATHNAME } from '@/app/consts/pathnames';
import { usePartnerCode } from '@/app/hooks/usePartnerCode';
import usePartnerUtm from '@/app/hooks/usePartnerUtm';

import Layout from './Layout';
import ProductSchema from './ProductSchema';

interface WizardPageProps {
  readonly accessory?: string;
  readonly children: ReactNode;
  readonly locale: string;
  readonly name: string;
}

const WizardPage = ({
  accessory,
  children,
  locale,
  name,
}: WizardPageProps): JSX.Element => {
  const currentRoute: CurrentRouteValues = useCurrentRoute({
    accessory,
    locale,
  });
  const { error, getFirstStockedStyle, getFirstStockedFinish } =
    usePDPContext();

  // Persist UTM query param data to localStorage (if any params exist)
  usePartnerUtm();

  // Persist partner code query param if exists
  usePartnerCode();

  if (error) {
    redirect(POP_PATHNAME);
  }

  const { isValid, redirectUrl } = checkCurrentRoute({
    ...currentRoute,
    getFirstStockedStyle,
    getFirstStockedFinish,
  });

  if (!isValid && redirectUrl) {
    redirect(redirectUrl);
  }

  return (
    <WizardStateProvider currentRoute={currentRoute}>
      <div data-cy={name}>
        <ProductSchema />
        <Layout>{children}</Layout>
      </div>
    </WizardStateProvider>
  );
};

export default WizardPage;
